import { useTranslation } from "react-i18next";
import { useEffect, useState, useRef } from "react";
import arrowDown from "../../../assets/icons/svg/arrowDown.svg";
import close from "../../../assets/icons/svg/close.svg";
import maximize from "../../../assets/icons/svg/Maximize.svg";
import { useDispatch, useSelector } from "react-redux";
import tickMark from "../../../assets/icons/svg/greenTickMark.svg";
import arrowUp from "../../../assets/icons/svg/ArrowsUp.svg";
import { maximiseFileUpload, removeFileUploadData } from "../../../actions/fileUpload";
import { getFileImage } from "../../../services/utils/iconMap";
import FileDelete from "../FileDelete/FileDelete";
import ErrorBanner from "./ErrorBanner";
import PropTypes from "prop-types";
import ButtonComponent from "@CubeComponents/form/ButtonComponent";

const EXPANDED_VIEW = "EXPANDED_VIEW";
const COMPACT_VIEW = "COMPACT_VIEW";

function UploadFileMinimiser({ handleCloseFileUploadModal, showErrorBanner, onDismiss, onReadMore }) {
  const { t } = useTranslation();
  const [view, setView] = useState(COMPACT_VIEW);
  const dispatch = useDispatch();
  const { fileUploadList, fileUploadState, spaceData } = useSelector((state) => state);

  const [openDeleteConfirmationModal, setOpenDeleteConfirmationModal] = useState(false);
  const [fileIndex] = useState(null);
  const selectedFile = useRef(null);

  const [files, setFiles] = useState([]);

  useEffect(() => {
    setFiles(fileUploadList.filter((file) => !file.isDeleted && !file.isRemoved));
  }, [fileUploadList]);

  const handleArrowDown = () => {
    setView((previous) => {
      if (previous === COMPACT_VIEW) {
        return EXPANDED_VIEW;
      }
      return COMPACT_VIEW;
    });
  };

  const handleClose = () => {
    handleCloseFileUploadModal();
  };

  const getProgress = () => {
    const progress = files.filter((file) => file.progressCompleted).length / files.length;
    return progress * 100;
  };

  const handleDeleteModalClose = () => {
    setOpenDeleteConfirmationModal(false);
  };

  const handleMaximise = () => {
    dispatch(maximiseFileUpload());
  };

  const onDeleteFiles = () => [fileUploadList[fileIndex].controller.forEach((item) => item.abort())];

  const onDeleteSuccess = () => {
    handleDeleteModalClose();
    dispatch(removeFileUploadData(fileIndex));
  };

  const headerTemplate = () => (
    <div className="flex flex-col gap-2 mb-2">
      <div className="flex flex-row justify-between items-center">
        <p className="text-2xl font-semibold">{`Uploading ${files.length} items`}</p>

        <div className="flex flex-row gap-4 items-center">
          <ButtonComponent
            onClick={handleMaximise}
            aria-label="Maximize"
            className="h-5 w-5 cursor-pointer bg-transparent border-none p-0"
          >
            <img src={maximize} className="h-5 w-5" alt="" />
          </ButtonComponent>

          <ButtonComponent
            onClick={handleArrowDown}
            aria-label="Toggle View"
            className="h-5 w-5 cursor-pointer bg-transparent border-none p-0"
          >
            <img src={view === EXPANDED_VIEW ? arrowDown : arrowUp} className="h-5 w-5" alt="" />
          </ButtonComponent>

          <ButtonComponent
            onClick={handleClose}
            aria-label="Close"
            className="h-4 w-4 cursor-pointer bg-transparent border-none p-0"
          >
            <img src={close} className="h-4 w-4" alt="" />
          </ButtonComponent>
        </div>
      </div>

      {showErrorBanner && <ErrorBanner onDismiss={onDismiss} onReadMore={onReadMore} />}
      <p className="text-base text-gray-700">
        {`Completed (${files.filter((file) => file.progressCompleted).length}/${files.length})`}
      </p>
    </div>
  );

  const uploadCompleted = () => (
    <div className="flex gap-1 items-center">
      <img src={tickMark} className="h-5 w-5" />
      <div className="text-base">{t("Upload Complete")}</div>
    </div>
  );

  const uploadError = (error) => <div className="text-xs text-red-500">{error}</div>;

  const getFilesProgress = () => (
    <div>
      {files.map((file, fileIndex) => {
        const getProgressPercentage = () => {
          if (file.progress === 100) {
            return file.progressCompleted ? 100 : 99;
          }
          return file.progress;
        };

        const progressPercentage = getProgressPercentage();

        return (
          <div key={`${file.name}_${fileIndex}`} className="border border-green-100 rounded-lg p-4 mb-4 bg-white">
            <div className="flex items-center mb-4 gap-2.5">
              <img src={getFileImage(file)} className="w-15 h-15" alt="" />
              <div className="flex flex-col gap-2.5 grow">
                <div className="flex justify-between items-center">
                  <p className="text-base font-bold">{file.name}</p>
                </div>
                <div>
                  {file.error ? uploadError(file.error) : file.progressCompleted ? uploadCompleted() : null}
                  {file.duplicateFileStatus?.found === false &&
                    file.error === false &&
                    file.progressCompleted === false &&
                    file.triggerFileUpload && (
                      <div key={`progress_${file.name}`} className="relative w-3/5 pt-3">
                        <div className="grid grid-cols-2 justify-between w-full absolute end-0 bottom-1 text-xs">
                          <div>{file.status}</div>
                          <div className="text-end">{progressPercentage}%</div>
                        </div>
                        <div className="bg-gray-300 w-full rounded-full h-0.5">
                          <div
                            className="bg-blue-500 h-0.5 rounded-full"
                            style={{
                              width: `${progressPercentage}%`,
                            }}
                          />
                        </div>
                      </div>
                    )}
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );

  return (
    <>
      {fileUploadState === "MINIMISED" && files.length ? (
        <div className="absolute bottom-9 end-9 z-50">
          {view === EXPANDED_VIEW ? (
            <div className="bg-white border border-gray-300 rounded shadow-lg z-50 w-96 h-96 flex flex-col">
              <div className="flex-shrink-0 p-6 pb-4 border-b border-gray-200 bg-white">{headerTemplate()}</div>
              <div className="flex-1 overflow-y-auto overflow-x-hidden p-6 pt-4">{getFilesProgress()}</div>
            </div>
          ) : (
            <div className="flex flex-col max-h-30 w-96 mb-5 border border-gray-300 p-6 bg-white shadow-lg">
              {headerTemplate()}
              <div className="bg-gray-300 w-full rounded-full h-0.5">
                <div className="bg-blue-500 h-0.5 mb-2.5 rounded-full" style={{ width: `${getProgress()}%` }} />
              </div>
            </div>
          )}
        </div>
      ) : null}
      {openDeleteConfirmationModal && (
        <FileDelete
          workspaceId={spaceData.id}
          selectedFiles={[selectedFile.current].map((file) => {
            file.hierarchyId = file.fileId;
            return file;
          })}
          onCloseModal={handleDeleteModalClose}
          onDeleteSuccess={onDeleteSuccess}
          isFileUpload
          onDeleteFiles={onDeleteFiles}
        />
      )}
    </>
  );
}

UploadFileMinimiser.propTypes = {
  handleCloseFileUploadModal: PropTypes.func.isRequired,
  showErrorBanner: PropTypes.bool,
  onDismiss: PropTypes.func,
  onReadMore: PropTypes.func,
};

UploadFileMinimiser.defaultProps = {
  showErrorBanner: false,
  onDismiss: () => {},
  onReadMore: () => {},
};

export default UploadFileMinimiser;
